import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { ApiError } from "../api/types";
import type { ProposedRevision } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { NewProposalDialog } from "../components/NewProposalDialog";
import { ProposalCard } from "../components/ProposalCard";
import { Button } from "../compose/controls";
import { Inbox, LibraryBig, Repeat2, ShieldCheck } from "../compose/Icon";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { useToast } from "../compose/Toast";
import { useAsync } from "../lib/useAsync";
import { SHOW_TEST_TOOLS } from "../lib/env";
import "../styles/proposals.css";

type StatusFilter = "pending" | "accepted" | "rejected" | "all";

const STATUS_SEGMENTS: SegmentedOption<StatusFilter>[] = [
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const DECIDER_ROLES = ["editor", "workspace-admin", "staff"];

/** One article-unit bucket of proposals (IA article-first sweep, design §3). A
 *  `unitId === null` group is the loose/legacy "Other documents" trailer. */
export interface ProposalGroup {
  unitId: number | null;
  title: string;
  items: ProposedRevision[];
}

/**
 * Bin a workspace's proposals by the article unit their revised document belongs
 * to, preserving the server's newest-first order (units appear in first-seen
 * order; loose/legacy proposals collect into a single trailing "Other documents"
 * group). Pure + exported so the grouping is unit-testable in isolation.
 */
export function groupProposals(list: ProposedRevision[]): ProposalGroup[] {
  const groups: ProposalGroup[] = [];
  const byUnit = new Map<number, ProposalGroup>();
  let loose: ProposalGroup | null = null;

  for (const p of list) {
    if (p.article_unit_id != null) {
      let g = byUnit.get(p.article_unit_id);
      if (!g) {
        g = {
          unitId: p.article_unit_id,
          title: p.unit_title ?? p.document_title,
          items: [],
        };
        byUnit.set(p.article_unit_id, g);
        groups.push(g);
      }
      g.items.push(p);
    } else {
      if (!loose) loose = { unitId: null, title: "Other documents", items: [] };
      loose.items.push(p);
    }
  }
  if (loose) groups.push(loose);
  return groups;
}

/**
 * Proposals `/w/:slug/proposals` (component-map §Proposals). The agent-proposed-
 * revision queue: each pending proposal in its teal "machine" coat with a before/
 * after diff and — for a human editor+ — Accept / Reject. A reviewer sees the
 * queue read-only; a member below the reviewer tier is fenced out server-side
 * (403 → the reviewer-access empty state). Staff/editor can seed a test proposal
 * while the agent-loop that authors them is a later track.
 */
export function Proposals() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user, memberships } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as StatusFilter) ?? "pending";

  const role = memberships.find((m) => m.workspace_slug === slug)?.role ?? "";
  const canDecide = Boolean(user?.is_staff) || DECIDER_ROLES.includes(role);

  const [creating, setCreating] = useState(false);
  const [decidingId, setDecidingId] = useState<number | null>(null);

  const proposals = useAsync(
    () => api.listProposals(slug, status === "all" ? undefined : status),
    [slug, status],
  );
  const forbidden =
    proposals.error instanceof ApiError && proposals.error.status === 403;

  const setStatus = (next: StatusFilter) => {
    const p = new URLSearchParams(params);
    if (next === "pending") p.delete("status");
    else p.set("status", next);
    setParams(p, { replace: true });
  };

  async function decide(p: ProposedRevision, action: "accept" | "reject") {
    setDecidingId(p.id);
    try {
      const updated =
        action === "accept"
          ? await api.acceptProposal(p.id)
          : await api.rejectProposal(p.id);
      toast.show(
        `Proposal ${updated.status === "accepted" ? "accepted" : "rejected"}`,
      );
      proposals.reload();
    } catch (err) {
      toast.show(
        err instanceof ApiError && err.status === 409
          ? "This proposal was already decided."
          : "Could not update the proposal.",
      );
    } finally {
      setDecidingId(null);
    }
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Proposals</h1>
          <p className="page__sub">
            Agent-proposed revisions awaiting a human decision.
          </p>
        </div>
        {SHOW_TEST_TOOLS && canDecide && !forbidden ? (
          <Button
            variant="primary"
            data-testid="new-proposal"
            icon={<Repeat2 size={15} strokeWidth={1.5} />}
            onClick={() => setCreating(true)}
          >
            New test proposal
          </Button>
        ) : null}
      </div>

      {!forbidden ? (
        <div className="docbar">
          <SegmentedControl
            ariaLabel="Filter proposals by status"
            value={status}
            options={STATUS_SEGMENTS}
            segmentTestIdPrefix="filter"
            onChange={setStatus}
          />
          <span className="page__sub" style={{ margin: 0 }}>
            {proposals.data
              ? `${proposals.data.length} proposal${proposals.data.length === 1 ? "" : "s"}`
              : ""}
          </span>
        </div>
      ) : null}

      {forbidden ? (
        <EmptyState
          icon={<ShieldCheck size={30} strokeWidth={1.5} />}
          title="Reviewer access"
          body="The proposal queue is open to reviewers and editors. Ask an admin to change your role."
        />
      ) : proposals.loading ? (
        <div className="proposal-list">
          <SkeletonRows rows={3} />
        </div>
      ) : proposals.error ? (
        <ErrorState body="We couldn't load the proposal queue." />
      ) : proposals.data && proposals.data.length > 0 ? (
        <div className="proposal-groups" data-testid="proposal-list">
          {groupProposals(proposals.data).map((group) => (
            <section
              className="proposal-group"
              key={group.unitId ?? "loose"}
              data-testid={`proposal-group-${group.unitId ?? "loose"}`}
            >
              <header className="proposal-group__head">
                {group.unitId != null ? (
                  <Link
                    to={`/w/${slug}/article/${group.unitId}`}
                    className="proposal-group__title"
                    data-testid={`proposal-group-link-${group.unitId}`}
                  >
                    <LibraryBig size={14} strokeWidth={1.5} />
                    {group.title}
                  </Link>
                ) : (
                  <span className="proposal-group__title proposal-group__title--loose">
                    {group.title}
                  </span>
                )}
                <span className="proposal-group__count">
                  {group.items.length} proposal
                  {group.items.length === 1 ? "" : "s"}
                </span>
              </header>
              <div className="proposal-list">
                {group.items.map((p) => (
                  <ProposalCard
                    key={p.id}
                    proposal={p}
                    slug={slug}
                    canDecide={canDecide}
                    busy={decidingId === p.id}
                    onAccept={(x) => decide(x, "accept")}
                    onReject={(x) => decide(x, "reject")}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Inbox size={30} strokeWidth={1.5} />}
          title={status === "pending" ? "No proposals waiting" : "Nothing here"}
          body={
            status === "pending"
              ? "When an agent proposes a revision, it lands here for a human to accept or reject."
              : "No proposals match this filter."
          }
        />
      )}

      {SHOW_TEST_TOOLS && creating ? (
        <NewProposalDialog
          slug={slug}
          onClose={() => setCreating(false)}
          onCreated={() => proposals.reload()}
        />
      ) : null}
    </div>
  );
}
